import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Card,
  CardContent,
  LinearProgress,
  Chip,
  Alert,
    CircularProgress,
  Radio,
  RadioGroup,
  FormControlLabel,
  IconButton,
  Fade,
  Slide,
  Zoom,
  useTheme,
  alpha,
} from '@mui/material';

import { styled, keyframes } from '@mui/material/styles';
import {
  PlayArrow,
  Psychology as Brain,
  TrendingUp,
  CheckCircle,
  Cancel,
  SkipNext,
  Calculate,
  Science,
  Language,
  Public as Globe,

  Star,
  Bolt,
  Fullscreen,
  FullscreenExit,
  GpsFixed as Target,
  EmojiEvents as Award,
  FlashOn as Zap,
} from '@mui/icons-material';

import { adaptiveTestingService } from '../services/adaptiveTestingService';
import { useAuth } from '../contexts/AuthContext';
import ActiveSessionDialog from '../components/ActiveSessionDialog';
import UpgradePrompt from '../components/UpgradePrompt';
import subscriptionService, { SubscriptionStatus } from '../services/subscriptionService';
import {
  StartTestRequest,
  StartTestResponse,
  SubmitAnswerRequest,
  SubmitAnswerResponse,
  Subject,
} from '../types';

// Modern Animations
const floatAnimation = keyframes`
  0%, 100% { transform: translateY(0px); }
  50% { transform: translateY(-10px); }
`;

const glowPulse = keyframes`
  0%, 100% { box-shadow: 0 0 20px rgba(102, 126, 234, 0.2); }
  50% { box-shadow: 0 0 40px rgba(102, 126, 234, 0.4); }
`;

const slideUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(40px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;


// Modern Styled Components
const TestContainer = styled(Box)`
  min-height: 100vh;
  background: #f8fafc;
  padding: 1rem;
`;

const ModernCard = styled(Card)`
  background: white;
  border-radius: 20px;
  border: 1px solid #e2e8f0;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.05);
  animation: ${slideUp} 0.6s ease-out;
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 12px 48px rgba(0, 0, 0, 0.1);
  }
`;

const QuestionCard = styled(Card)`
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border-radius: 20px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
  animation: ${slideUp} 0.6s ease-out;
`;

const ProgressCard = styled(Card)`
  background: white;
  border-radius: 16px;
  border: 1px solid #e2e8f0;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.05);
  margin-bottom: 1.5rem;
`;

const ModernButton = styled(Button)`
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border-radius: 12px;
  padding: 12px 24px;
  font-weight: 600;
  text-transform: none;
  box-shadow: 0 4px 16px rgba(102, 126, 234, 0.3);
  transition: all 0.3s ease;
  
  &:hover {
    background: linear-gradient(135deg, #5a67d8 0%, #6b46c1 100%);
    transform: translateY(-2px);
    box-shadow: 0 6px 24px rgba(102, 126, 234, 0.4);
  }
  
  &:active {
    transform: translateY(0);
  }

  &.Mui-disabled {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: white;
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const OptionCard = styled(Card)<{ selected?: boolean; correct?: boolean; incorrect?: boolean }>`
  margin-bottom: 12px;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.3s ease;
  border: 2px solid transparent;
  
  ${({ selected, correct, incorrect }) => {
    if (correct) return `
      background: linear-gradient(135deg, #10b981, #059669);
      color: white;
      border-color: #10b981;
      transform: scale(1.02);
      box-shadow: 0 6px 24px rgba(16, 185, 129, 0.3);
    `;
    if (incorrect) return `
      background: linear-gradient(135deg, #ef4444, #dc2626);
      color: white;
      border-color: #ef4444;
      transform: scale(0.98);
      box-shadow: 0 6px 24px rgba(239, 68, 68, 0.3);
    `;
    if (selected) return `
      background: linear-gradient(135deg, #667eea, #764ba2);
      color: white;
      border-color: #667eea;
      transform: scale(1.01);
      box-shadow: 0 6px 24px rgba(102, 126, 234, 0.3);
    `;
    return `
      background: white;
      color: #334155;
      border-color: #e2e8f0;
      
      &:hover {
        background: #f8fafc;
        border-color: #667eea;
        transform: translateY(-2px);
        box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
      }
    `;
  }}
`;

const StatsCard = styled(Card)<{ color?: string }>`
  background: ${({ color }) => 
    color ? `linear-gradient(135deg, ${color}15 0%, ${color}05 100%)` : 
    `linear-gradient(135deg, #667eea15 0%, #667eea05 100%)`
  };
  border: 1px solid ${({ color }) => 
    color ? alpha(color, 0.1) : alpha('#667eea', 0.1)
  };
  border-radius: 16px;
  text-align: center;
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: ${({ color }) => 
      color ? `0 4px 20px ${alpha(color, 0.15)}` : 
      `0 4px 20px ${alpha('#667eea', 0.15)}`
    };
  }
`;

const SubjectCard = styled(Card)<{ selected?: boolean; color?: string }>`
  cursor: pointer;
  transition: all 0.3s ease;
  border-radius: 16px;
  background: ${({ selected, color }) => 
    selected && color ? `linear-gradient(135deg, ${color}15 0%, ${color}05 100%)` : 'white'
  };
  border: 1px solid ${({ selected, color }) => 
    selected && color ? alpha(color, 0.2) : '#e2e8f0'
  };
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: ${({ color }) => 
      color ? `0 4px 20px ${alpha(color, 0.15)}` : 
      `0 4px 20px ${alpha('#667eea', 0.15)}`
    };
    border-color: ${({ color }) => color || '#667eea'};
  }
`;

const AnimatedIcon = styled(Box)`
  animation: ${floatAnimation} 3s ease-in-out infinite;
  display: inline-block;
`;

const GlowingChip = styled(Chip)`
  background: rgba(255, 255, 255, 0.2);
  color: white;
  border: 1px solid rgba(255, 255, 255, 0.3);
  animation: ${glowPulse} 2s ease-in-out infinite;
`;

// Test phases
type TestPhase = 'setup' | 'testing' | 'feedback' | 'explanation' | 'completed';

interface TestState {
  phase: TestPhase;
  sessionId?: string;
  currentQuestion?: any;
  answeredQuestion?: any; // Store the question that was just answered
  progress?: any;
  student?: any;
  selectedAnswer?: string;
  showFeedback: boolean;
  lastResponse?: SubmitAnswerResponse;
  timer: number;
  isPaused: boolean;
}

interface ActiveSession {
  sessionId: string;
  subject: string;
  grade: number;
  startTime: string;
  progress: {
    current: number;
    total: number;
    percentage: number;
  };
  score: number;
  currentQuestion?: any;
}

const AdaptiveTestPage: React.FC = () => {
  const { student } = useAuth();
  const theme = useTheme();
  
  // State management
  const [testState, setTestState] = useState<TestState>({
    phase: 'setup',
    showFeedback: false,
    timer: 0,
    isPaused: false,
  });

  const [selectedSubject, setSelectedSubject] = useState<Subject>('math');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Active session management
  const [activeSession, setActiveSession] = useState<ActiveSession | null>(null);
  const [showActiveSessionDialog, setShowActiveSessionDialog] = useState(false);
  const [sessionLoading, setSessionLoading] = useState(false);
  
  // Subscription state
  const [subscriptionStatus, setSubscriptionStatus] = useState<SubscriptionStatus | null>(null);
  const [showUpgradePrompt, setShowUpgradePrompt] = useState(false);

  // Fullscreen state
  const [isFullscreen, setIsFullscreen] = useState(false);

  // Fullscreen helper functions
  const enterFullscreen = async () => {
    try {
      if (document.documentElement.requestFullscreen) {
        await document.documentElement.requestFullscreen();
      } else if ((document.documentElement as any).webkitRequestFullscreen) {
        await (document.documentElement as any).webkitRequestFullscreen();
      } else if ((document.documentElement as any).msRequestFullscreen) {
        await (document.documentElement as any).msRequestFullscreen();
      }
      setIsFullscreen(true);
    } catch (error) {
      console.warn('Could not enter fullscreen mode:', error);
    }
  };

  const exitFullscreen = async () => {
    try {
      if (document.exitFullscreen) {
        await document.exitFullscreen();
      } else if ((document as any).webkitExitFullscreen) {
        await (document as any).webkitExitFullscreen();
      } else if ((document as any).msExitFullscreen) {
        await (document as any).msExitFullscreen();
      }
      setIsFullscreen(false);
    } catch (error) {
      console.warn('Could not exit fullscreen mode:', error);
    }
  };

  // Listen for fullscreen changes
  useEffect(() => {
    const handleFullscreenChange = () => {
      const isCurrentlyFullscreen = !!(document.fullscreenElement || 
        (document as any).webkitFullscreenElement || 
        (document as any).msFullscreenElement);
      setIsFullscreen(isCurrentlyFullscreen);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    document.addEventListener('msfullscreenchange', handleFullscreenChange);

    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
      document.removeEventListener('msfullscreenchange', handleFullscreenChange);
    };
  }, []);

  // Check for active session and subscription status on component mount
  useEffect(() => {
    checkActiveSession();
    fetchSubscriptionStatus();
  }, []);

  // Exit fullscreen when test is completed
  useEffect(() => {
    if (testState.phase === 'completed' && isFullscreen) {
      exitFullscreen();
    }
  }, [testState.phase, isFullscreen]);

  // Show notification when user exits fullscreen during testing
  const showFullscreenReminder = testState.phase === 'testing' && !isFullscreen;

  // Fetch subscription status
  const fetchSubscriptionStatus = async () => {
    try {
      const status = await subscriptionService.getSubscriptionStatus();
      setSubscriptionStatus(status);
    } catch (error) {
      console.error('Error fetching subscription status:', error);
    }
  };

  // Handle upgrade success
  const handleUpgradeSuccess = async () => {
    await fetchSubscriptionStatus();
    setShowUpgradePrompt(false);
  };

  // Get subscription info for a subject
  const getSubjectSubscriptionInfo = (subject: Subject) => {
    if (!subscriptionStatus) return null;
    
    const limits = subscriptionStatus.testLimits[subject];
    if (!limits) return null;

    if (subscriptionStatus.plan !== 'free') {
      return (
        <Chip 
          label="Unlimited" 
          size="small" 
          color="success" 
          sx={{ mt: 1 }}
        />
      );
    }

    const remaining = limits.remaining;
    const color = remaining === 0 ? 'error' : remaining === 1 ? 'warning' : 'info';
    
    return (
      <Chip 
        label={`${remaining} test${remaining !== 1 ? 's' : ''} left`}
        size="small" 
        color={color}
        sx={{ mt: 1 }}
      />
    );
  };

  // Timer effect
  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (testState.phase === 'testing' && !testState.isPaused) {
      interval = setInterval(() => {
        setTestState(prev => ({ ...prev, timer: prev.timer + 1 }));
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [testState.phase, testState.isPaused]);

  // Scroll to top on phase change
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [testState.phase]);

  // Check for active session
  const checkActiveSession = async () => {
    try {
      const token = localStorage.getItem('apex_auth_token');
      const response = await fetch('/api/adaptive-testing/check-active-session', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success && data.hasActiveSession) {
          setActiveSession(data.session);
          setShowActiveSessionDialog(true);
        }
      }
    } catch (error) {
      console.error('Error checking active session:', error);
    }
  };

  // Resume active session
  const handleResumeSession = async () => {
    setSessionLoading(true);
    try {
      const token = localStorage.getItem('apex_auth_token');
      const response = await fetch('/api/adaptive-testing/resume-session', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setTestState({
            phase: 'testing',
            sessionId: data.sessionId,
            currentQuestion: data.currentQuestion,
            progress: data.progress,
            student: data.student,
            showFeedback: false,
            timer: 0,
            isPaused: false,
          });
          setShowActiveSessionDialog(false);
          setActiveSession(null);
          
          // Enter fullscreen mode when resuming
          await enterFullscreen();
        }
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to resume session');
      }
    } catch (error: any) {
      setError(error.message || 'Failed to resume session');
    } finally {
      setSessionLoading(false);
    }
  };

  // End active session
  const handleEndSession = async (saveProgress: boolean) => {
    setSessionLoading(true);
    try {
      const token = localStorage.getItem('apex_auth_token');
      const response = await fetch('/api/adaptive-testing/end-session', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ saveProgress })
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setShowActiveSessionDialog(false);
          setActiveSession(null);
          // Show success message
          if (saveProgress) {
            setError(null);
            // Trigger a dashboard stats refresh by dispatching a custom event
            window.dispatchEvent(new CustomEvent('statsUpdated'));
            // Could show a success toast here
          }
        }
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to end session');
      }
    } catch (error: any) {
      setError(error.message || 'Failed to end session');
    } finally {
      setSessionLoading(false);
    }
  };



  // Start test
  const handleStartTest = async () => {
    if (!student) {
      setError('Student data not found. Please refresh the page.');
      return;
    }

    // Check subscription limits first
    if (subscriptionStatus && !subscriptionStatus.testLimits[selectedSubject]?.canTake) {
      setShowUpgradePrompt(true);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const testRequest: StartTestRequest = {
        subject: selectedSubject,
      };

      const response: StartTestResponse = await adaptiveTestingService.startTest(testRequest);
      
      setTestState({
        phase: 'testing',
        sessionId: response.sessionId,
        currentQuestion: response.currentQuestion,
        progress: response.progress,
        student: response.student,
        showFeedback: false,
        timer: 0,
        isPaused: false,
      });
      
      // Enter fullscreen mode
      await enterFullscreen();
      
      // Refresh subscription status to update remaining tests
      await fetchSubscriptionStatus();
    } catch (err: any) {
      // Check if error is about subscription limits
      if (err.message?.includes('test limit') || err.message?.includes('upgrade')) {
        setShowUpgradePrompt(true);
      } else {
        setError(err.message || 'Failed to start test');
      }
    } finally {
      setLoading(false);
    }
  };

  // Poll for next question when it's being generated
  const pollForNextQuestion = async (retryCount = 0) => {
    if (!testState.sessionId || retryCount > 10) {
      console.error('❌ Max retries reached or no session ID');
      setError('Failed to load next question. Please refresh and try again.');
      return;
    }
    
    try {
      console.log(`🔄 Polling for next question (attempt ${retryCount + 1})`);
      
      const response = await fetch(`/api/adaptive-testing/next-question`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('apex_auth_token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ sessionId: testState.sessionId })
      });

      if (response.ok) {
        const data = await response.json();
        console.log('📥 Next question response:', data);
        
        if (data.success && data.currentQuestion) {
          console.log('✅ Got next question, updating state');
          setTestState(prev => ({
            ...prev,
            currentQuestion: data.currentQuestion,
            progress: data.progress,
            phase: 'explanation',
            nextQuestionLoading: false
          }));
        } else if (data.testCompleted) {
          console.log('🏁 Test completed');
          setTestState(prev => ({
            ...prev,
            phase: 'completed',
            lastResponse: { ...prev.lastResponse, ...data },
            nextQuestionLoading: false
          }));
        } else {
          // Still no question available, try again with exponential backoff
          const delay = Math.min(2000 + (retryCount * 1000), 8000); // 2s, 3s, 4s, ... max 8s
          console.log(`⏳ Still no question available, retrying in ${delay}ms...`);
          setTimeout(() => pollForNextQuestion(retryCount + 1), delay);
        }
      } else {
        console.error('❌ Next question request failed:', response.status);
        setTimeout(() => pollForNextQuestion(retryCount + 1), 2000);
      }
    } catch (error) {
      console.error('Error polling for next question:', error);
      setTimeout(() => pollForNextQuestion(retryCount + 1), 2000);
    }
  };

  // Go to next question
  const handleNextQuestion = () => {
    setTestState(prev => ({
      ...prev,
      phase: 'testing',
      selectedAnswer: undefined,
      showFeedback: false,
    }));
  };

  // Submit answer
  const handleSubmitAnswer = async () => {
    if (!testState.selectedAnswer || !testState.sessionId || !testState.currentQuestion) {
      return;
    }

    setLoading(true);

    try {
      const request: SubmitAnswerRequest = {
        sessionId: testState.sessionId,
        questionId: testState.currentQuestion.id,
        answer: testState.selectedAnswer,
        timeTaken: testState.timer,
      };

      const response: SubmitAnswerResponse = await adaptiveTestingService.submitAnswer(request);
      
      console.log('📥 Submit answer response:', response);

      setTestState(prev => ({
        ...prev,
        phase: response.testCompleted ? 'completed' : 'feedback',
        lastResponse: response,
        showFeedback: true,
        timer: 0,
        answeredQuestion: prev.currentQuestion, // Save the question that was just answered
      }));

      // Handle different response scenarios
      if (response.testCompleted) {
        // Test is completed
        return;
      } else if ((response as any).nextQuestionPending) {
        // Next question is being generated, show feedback and wait
        console.log('🔄 Next question is pending, starting retry mechanism');
        setTestState(prev => ({
          ...prev,
          phase: 'explanation',
          progress: response.progress,
          nextQuestionLoading: true,
        }));
        
        // Start polling for next question after a short delay
        setTimeout(() => {
          console.log('🔄 Starting to poll for next question...');
          pollForNextQuestion(0);
        }, 3000); // Wait 3 seconds for AI generation
      } else {
        // Normal case - next question is available
        setTestState(prev => ({
          ...prev,
          phase: 'explanation',
          currentQuestion: response.currentQuestion,
          progress: response.progress,
        }));
      }
    } catch (err: any) {
      console.error('❌ Submit answer error:', err);
      setError(err.message || 'Failed to submit answer');
    } finally {
      setLoading(false);
    }
  };



  // Render setup form
  const renderSetupForm = () => (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Fade in timeout={800}>
        <ModernCard>
          <CardContent sx={{ p: 4 }}>
            <Box textAlign="center" mb={4}>
              <AnimatedIcon>
                <Brain sx={{ fontSize: 64, color: '#667eea', mb: 2 }} />
              </AnimatedIcon>
              <Typography variant="h3" fontWeight={800} gutterBottom sx={{ 
                background: 'linear-gradient(135deg, #667eea, #764ba2)',
                backgroundClip: 'text',
                textFillColor: 'transparent',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent'
              }}>
                Welcome, {student?.name}! 🎯
              </Typography>
              <Typography variant="h6" color="text.secondary" mb={2}>
                Choose your subject for an AI-powered adaptive learning experience
              </Typography>
              <Box sx={{ 
                display: 'inline-flex', 
                alignItems: 'center', 
                gap: 1,
                background: 'linear-gradient(135deg, #667eea, #764ba2)',
                color: 'white',
                px: 3,
                py: 1,
                borderRadius: 20,
                fontSize: '0.9rem',
                fontWeight: 600
              }}>
                <Star sx={{ fontSize: 20 }} />
                Grade {student?.grade} • {student?.school || 'Student'}
              </Box>
            </Box>

            <Box sx={{ 
              display: 'grid', 
              gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, 
              gap: 3,
              mb: 4
            }}>
              <SubjectCard 
                selected={selectedSubject === 'math'}
                color={theme.palette.primary.main}
                onClick={() => setSelectedSubject('math')}
              >
                <CardContent sx={{ p: 3 }}>
                  <Box display="flex" alignItems="center" gap={2} mb={2}>
                    <Box sx={{ 
                      width: 50, 
                      height: 50, 
                      borderRadius: 2, 
                      backgroundColor: alpha(theme.palette.primary.main, 0.1),
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: theme.palette.primary.main
                    }}>
                      <Calculate sx={{ fontSize: 24 }} />
                    </Box>
                    <Typography variant="h6" fontWeight={700}>Mathematics</Typography>
                    <Bolt sx={{ color: theme.palette.primary.main, fontSize: 20 }} />
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    Algebra, Geometry, Arithmetic, and Problem Solving
                  </Typography>
                  {getSubjectSubscriptionInfo('math')}
                </CardContent>
              </SubjectCard>

              <SubjectCard 
                selected={selectedSubject === 'science'}
                color={theme.palette.secondary.main}
                onClick={() => setSelectedSubject('science')}
              >
                <CardContent sx={{ p: 3 }}>
                  <Box display="flex" alignItems="center" gap={2} mb={2}>
                    <Box sx={{ 
                      width: 50, 
                      height: 50, 
                      borderRadius: 2, 
                      backgroundColor: alpha(theme.palette.secondary.main, 0.1),
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: theme.palette.secondary.main
                    }}>
                      <Science sx={{ fontSize: 24 }} />
                    </Box>
                    <Typography variant="h6" fontWeight={700}>Science</Typography>
                    <Zap sx={{ color: theme.palette.secondary.main, fontSize: 20 }} />
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    Physics, Chemistry, Biology, and Earth Science
                  </Typography>
                  {getSubjectSubscriptionInfo('science')}
                </CardContent>
              </SubjectCard>

              <SubjectCard 
                selected={selectedSubject === 'english'}
                color={theme.palette.success.main}
                onClick={() => setSelectedSubject('english')}
              >
                <CardContent sx={{ p: 3 }}>
                  <Box display="flex" alignItems="center" gap={2} mb={2}>
                    <Box sx={{ 
                      width: 50, 
                      height: 50, 
                      borderRadius: 2, 
                      backgroundColor: alpha(theme.palette.success.main, 0.1),
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: theme.palette.success.main
                    }}>
                      <Language sx={{ fontSize: 24 }} />
                    </Box>
                    <Typography variant="h6" fontWeight={700}>English</Typography>
                    <Target sx={{ color: theme.palette.success.main, fontSize: 20 }} />
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    Grammar, Vocabulary, Reading, and Comprehension
                  </Typography>
                  {getSubjectSubscriptionInfo('english')}
                </CardContent>
              </SubjectCard>

              <SubjectCard 
                selected={selectedSubject === 'gk'}
                color={theme.palette.warning.main}
                onClick={() => setSelectedSubject('gk')}
              >
                <CardContent sx={{ p: 3 }}>
                  <Box display="flex" alignItems="center" gap={2} mb={2}>
                    <Box sx={{ 
                      width: 50, 
                      height: 50, 
                      borderRadius: 2, 
                      backgroundColor: alpha(theme.palette.warning.main, 0.1),
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: theme.palette.warning.main
                    }}>
                      <Globe sx={{ fontSize: 24 }} />
                    </Box>
                    <Typography variant="h6" fontWeight={700}>General Knowledge</Typography>
                    <Award sx={{ color: theme.palette.warning.main, fontSize: 20 }} />
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    History, Geography, Current Affairs, and Culture
                  </Typography>
                  {getSubjectSubscriptionInfo('gk')}
                </CardContent>
              </SubjectCard>
            </Box>

            {error && (
              <Alert severity="error" sx={{ mt: 2, borderRadius: 2 }}>
                {error}
              </Alert>
            )}

            <Box textAlign="center" mt={4}>
              <ModernButton
                onClick={handleStartTest}
                disabled={loading}
                startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <PlayArrow />}
                sx={{ fontSize: '1.1rem', py: 1.5, px: 4 }}
              >
                {loading ? 'Starting Test...' : 'Start Adaptive Test'}
              </ModernButton>
            </Box>
          </CardContent>
        </ModernCard>
      </Fade>
    </Container>
  );

      // Render test interface
    const renderTestInterface = () => (
      <Container maxWidth="lg" sx={{ py: 3 }}>
        {/* Fullscreen Reminder */}
        {showFullscreenReminder && (
          <Fade in>
            <Alert 
              severity="info" 
              sx={{ mb: 2 }}
              action={
                <Button 
                  color="inherit" 
                  size="small" 
                  onClick={enterFullscreen}
                  startIcon={<Fullscreen />}
                >
                  Enter Fullscreen
                </Button>
              }
            >
              For the best test experience, we recommend using fullscreen mode.
            </Alert>
          </Fade>
        )}

        {/* Modern Progress Header */}
        <Slide in direction="down" timeout={600}>
        <ProgressCard>
          <CardContent sx={{ p: 3 }}>
            <Box sx={{ 
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              gap: 3
            }}>
              <Box display="flex" alignItems="center" gap={2}>
                <AnimatedIcon>
                  <Brain sx={{ fontSize: 40, color: '#667eea' }} />
                </AnimatedIcon>
                <Box>
                  <Typography variant="h6" fontWeight={700}>
                    {testState.student?.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Grade {student?.grade} • {selectedSubject.charAt(0).toUpperCase() + selectedSubject.slice(1)}
                  </Typography>
                </Box>
              </Box>
              
              <Box sx={{ flex: 1, mx: 3, minWidth: 250 }}>
                <Box display="flex" justifyContent="space-between" mb={1}>
                  <Typography variant="body1" fontWeight={700}>
                    Question {testState.progress?.current} of {testState.progress?.total}
                  </Typography>
                  <Typography variant="body1" color="text.secondary" fontWeight={600}>
                    {testState.progress?.percentage}% Complete
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={testState.progress?.percentage || 0}
                  sx={{
                    height: 8,
                    borderRadius: 4,
                    backgroundColor: 'rgba(102, 126, 234, 0.1)',
                    '& .MuiLinearProgress-bar': {
                      background: 'linear-gradient(135deg, #667eea, #764ba2)',
                      borderRadius: 4,
                      animation: `${glowPulse} 2s ease-in-out infinite`,
                    },
                  }}
                />
              </Box>
              
              <Box display="flex" gap={2}>
                <StatsCard color="#667eea" sx={{ minWidth: 80 }}>
                  <CardContent sx={{ p: 2 }}>
                    <Typography variant="h5" fontWeight={800} color="#667eea">
                      {testState.progress?.current || 0}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Current
                    </Typography>
                  </CardContent>
                </StatsCard>
                <StatsCard color="#10b981" sx={{ minWidth: 80 }}>
                  <CardContent sx={{ p: 2 }}>
                    <Typography variant="h5" fontWeight={800} color="#10b981">
                      {Math.floor(testState.timer / 60)}:{(testState.timer % 60).toString().padStart(2, '0')}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Time
                    </Typography>
                  </CardContent>
                </StatsCard>
                
                {/* Fullscreen Toggle Button */}
                <IconButton
                  onClick={isFullscreen ? exitFullscreen : enterFullscreen}
                  sx={{
                    bgcolor: alpha('#667eea', 0.1),
                    color: '#667eea',
                    '&:hover': {
                      bgcolor: alpha('#667eea', 0.2),
                    },
                  }}
                  title={isFullscreen ? 'Exit Fullscreen' : 'Enter Fullscreen'}
                >
                  {isFullscreen ? <FullscreenExit /> : <Fullscreen />}
                </IconButton>
              </Box>
            </Box>
          </CardContent>
        </ProgressCard>
      </Slide>

      {/* Single Column Layout */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {/* Enhanced Question Card */}
          <Zoom in timeout={800}>
            <QuestionCard>
              <CardContent sx={{ p: 3 }}>
                <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={3}>
                  <GlowingChip
                    label={testState.currentQuestion?.topic}
                    sx={{ fontWeight: 600 }}
                  />
                  <GlowingChip
                    label={`${testState.currentQuestion?.difficultyLevel}% Difficulty`}
                    sx={{ fontWeight: 600 }}
                  />
                </Box>
                <Typography variant="h5" fontWeight={700} sx={{ lineHeight: 1.4 }}>
                  {testState.currentQuestion?.text}
                </Typography>
              </CardContent>
            </QuestionCard>
          </Zoom>

          {/* Enhanced Answer Options */}
          <Fade in timeout={1000}>
            <ModernCard>
              <CardContent sx={{ p: 3 }}>
                <Box display="flex" alignItems="center" gap={2} mb={3}>
                  <Typography variant="h6" fontWeight={700}>
                    Choose your answer:
                  </Typography>
                  <Zap sx={{ color: '#667eea', fontSize: 20 }} />
                </Box>
                
                {/* Render True/False questions with radio buttons */}
                {(() => {
                  const options = testState.currentQuestion?.options;
                  const questionText = testState.currentQuestion?.text || '';
                  
                  // Check if it's a True/False question by options OR by question text pattern
                  const isTrueFalseByOptions = options?.length === 2 && 
                                              options?.includes('True') && 
                                              options?.includes('False');
                  
                  const isTrueFalseByText = questionText.toLowerCase().includes('true or false:') ||
                                           questionText.toLowerCase().includes('true/false:');
                  
                  return isTrueFalseByOptions || (isTrueFalseByText && (!options || options.length === 0));
                })() ? (
                  <RadioGroup
                    value={testState.selectedAnswer || ''}
                    onChange={(e) => setTestState(prev => ({ ...prev, selectedAnswer: e.target.value }))}
                    sx={{ gap: 2 }}
                  >
                    {(testState.currentQuestion.options?.length > 0 
                      ? testState.currentQuestion.options 
                      : ['True', 'False']
                    ).map((option: string) => (
                      <FormControlLabel
                        key={option}
                        value={option}
                        control={
                          <Radio 
                            sx={{
                              color: '#667eea',
                              '&.Mui-checked': {
                                color: '#667eea',
                              },
                            }}
                          />
                        }
                        label={
                          <Typography variant="body1" fontWeight={700} sx={{ ml: 1, fontSize: '1rem' }}>
                            {option}
                          </Typography>
                        }
                        sx={{
                          m: 0,
                          p: 2,
                          borderRadius: 2,
                          border: '2px solid',
                          borderColor: testState.selectedAnswer === option ? '#667eea' : '#e2e8f0',
                          backgroundColor: testState.selectedAnswer === option ? alpha('#667eea', 0.05) : 'transparent',
                          transition: 'all 0.3s ease',
                          '&:hover': {
                            borderColor: '#667eea',
                            backgroundColor: alpha('#667eea', 0.05),
                          },
                        }}
                      />
                    ))}
                  </RadioGroup>
                ) : (
                  /* Render multiple choice questions with cards in 2x2 grid */
                  <Box sx={{ 
                    display: 'grid', 
                    gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, 
                    gap: 2 
                  }}>
                    {testState.currentQuestion?.options?.map((option: string, index: number) => (
                      <OptionCard
                        key={index}
                        selected={testState.selectedAnswer === option}
                        onClick={() => setTestState(prev => ({ ...prev, selectedAnswer: option }))}
                      >
                        <CardContent sx={{ p: 2 }}>
                          <Box display="flex" alignItems="center" gap={2}>
                            <Box
                              sx={{
                                width: 32,
                                height: 32,
                                borderRadius: '50%',
                                background: testState.selectedAnswer === option 
                                  ? 'linear-gradient(135deg, #667eea, #764ba2)' 
                                  : 'linear-gradient(135deg, #f1f5f9, #e2e8f0)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontWeight: 700,
                                fontSize: '0.9rem',
                                color: testState.selectedAnswer === option ? 'white' : '#64748b',
                                transition: 'all 0.3s ease',
                                flexShrink: 0,
                              }}
                            >
                              {String.fromCharCode(65 + index)}
                            </Box>
                            <Typography variant="body1" fontWeight={700} sx={{ lineHeight: 1.3, fontSize: '1rem' }}>
                              {option}
                            </Typography>
                          </Box>
                        </CardContent>
                      </OptionCard>
                    ))}
                  </Box>
                )}

                <Box display="flex" justifyContent="center" mt={4}>
                  <ModernButton
                    onClick={handleSubmitAnswer}
                    disabled={!testState.selectedAnswer || loading}
                    startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <CheckCircle />}
                    sx={{ fontSize: '1.1rem', py: 1.5, px: 4 }}
                  >
                    {loading ? 'Submitting...' : 'Submit Answer'}
                  </ModernButton>
                </Box>
              </CardContent>
            </ModernCard>
          </Fade>
        </Box>
    </Container>
  );

  // Render enhanced explanation interface
  const renderExplanation = () => (
    <Container maxWidth="lg" sx={{ py: 3 }}>
      <Fade in timeout={600}>
        <Box>
          {/* Question Review Card */}
          <Zoom in timeout={800}>
            <QuestionCard sx={{ 
              mb: 3,
              background: testState.lastResponse?.isCorrect 
                ? 'linear-gradient(135deg, #10b981, #059669)' 
                : 'linear-gradient(135deg, #ef4444, #dc2626)'
            }}>
              <CardContent sx={{ p: 3 }}>
                <Box display="flex" alignItems="center" gap={2} mb={3}>
                  {testState.lastResponse?.isCorrect ? (
                    <CheckCircle sx={{ fontSize: 32, color: 'white' }} />
                  ) : (
                    <Cancel sx={{ fontSize: 32, color: 'white' }} />
                  )}
                  <Typography variant="h5" fontWeight={700} color="white">
                    {testState.lastResponse?.isCorrect ? 'Correct! Well done! 🎉' : 'Not quite right, but let\'s learn! 📚'}
                  </Typography>
                </Box>
                
                <GlowingChip
                  label={testState.answeredQuestion?.topic}
                  sx={{ mb: 2, background: 'rgba(255, 255, 255, 0.15)', '&:hover': { background: 'rgba(255, 255, 255, 0.25)'} }}
                />
                <Typography variant="h6" fontWeight={600} mb={2} color="white">
                  {testState.answeredQuestion?.text}
                </Typography>
                
                <Box sx={{ 
                  background: 'rgba(0, 0, 0, 0.15)',
                  borderRadius: 2,
                  p: 2
                }}>
                  <Typography variant="body1" color="rgba(255, 255, 255, 0.9)" mb={1}>
                    Your answer: <strong>{testState.selectedAnswer}</strong>
                  </Typography>
                  <Typography variant="body1" color="white">
                    Correct answer: <strong>{(() => {
                      const correctAnswer = testState.lastResponse?.correctAnswer;
                      const options = testState.answeredQuestion?.options;
                      
                      if (!correctAnswer || !options) return correctAnswer;
                      
                      if (correctAnswer.length === 1 && correctAnswer.match(/[A-D]/i)) {
                        const index = correctAnswer.toUpperCase().charCodeAt(0) - 65;
                        return options[index] || correctAnswer;
                      }
                      
                      return correctAnswer;
                    })()}</strong>
                  </Typography>
                </Box>
              </CardContent>
            </QuestionCard>
          </Zoom>

          {/* Enhanced Explanation Card */}
          <Fade in timeout={1000}>
            <ModernCard>
              <CardContent sx={{ p: 3 }}>
                <Box display="flex" alignItems="center" gap={2} mb={2}>
                  <AnimatedIcon>
                    <Brain sx={{ color: '#667eea', fontSize: 32 }} />
                  </AnimatedIcon>
                  <Typography variant="h5" fontWeight={700}>
                    Let's Learn Together! 🌟
                  </Typography>
                </Box>
                
                {testState.lastResponse?.explanation && (
                  <Box sx={{ 
                    background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.05), rgba(118, 75, 162, 0.05))',
                    borderRadius: 2,
                    p: 3,
                    mb: 3,
                    border: '1px solid rgba(102, 126, 234, 0.1)'
                  }}>
                    <Typography variant="body1" sx={{ 
                      lineHeight: 1.7,
                      whiteSpace: 'pre-line',
                      color: '#334155'
                    }}>
                      {testState.lastResponse.explanation}
                    </Typography>
                  </Box>
                )}

                <Box display="flex" justifyContent="center" mt={3}>
                  <ModernButton
                    onClick={handleNextQuestion}
                    disabled={(testState as any).nextQuestionLoading}
                    startIcon={(testState as any).nextQuestionLoading ? <CircularProgress size={20} color="inherit" /> : <SkipNext />}
                    sx={{ fontSize: '1.1rem', py: 1.5, px: 4 }}
                  >
                    {(testState as any).nextQuestionLoading ? 'Preparing Next Question...' : 'Got it! Next Question →'}
                  </ModernButton>
                </Box>
              </CardContent>
            </ModernCard>
          </Fade>
        </Box>
      </Fade>
    </Container>
  );

  // Render completion
  const renderCompletion = () => (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Fade in timeout={800}>
        <ModernCard>
          <CardContent sx={{ p: 4, textAlign: 'center' }}>
            <AnimatedIcon>
              <TrendingUp sx={{ fontSize: 64, color: '#667eea', mb: 3 }} />
            </AnimatedIcon>
            
            <Typography variant="h3" fontWeight={800} gutterBottom sx={{ 
              background: 'linear-gradient(135deg, #667eea, #764ba2)',
              backgroundClip: 'text',
              textFillColor: 'transparent',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              mb: 2
            }}>
              Test Completed! 🎉
            </Typography>
            
            <Typography variant="h6" color="text.secondary" mb={4}>
              Outstanding work {testState.student?.name}! You've completed the adaptive test.
            </Typography>

            <Box sx={{ 
              display: 'grid', 
              gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }, 
              gap: 3,
              mb: 4
            }}>
              <StatsCard color="#667eea">
                <CardContent sx={{ p: 3 }}>
                  <Box display="flex" alignItems="center" justifyContent="center" gap={1} mb={2}>
                    <Star sx={{ color: '#667eea', fontSize: 24 }} />
                    <Typography variant="h3" fontWeight={800} color="#667eea">
                      {testState.lastResponse?.finalScore}%
                    </Typography>
                  </Box>
                  <Typography variant="body1" color="text.secondary" fontWeight={600}>
                    Final Score
                  </Typography>
                </CardContent>
              </StatsCard>
              
              <StatsCard color="#10b981">
                <CardContent sx={{ p: 3 }}>
                  <Box display="flex" alignItems="center" justifyContent="center" gap={1} mb={2}>
                    <CheckCircle sx={{ color: '#10b981', fontSize: 24 }} />
                    <Typography variant="h3" fontWeight={800} color="#10b981">
                      {testState.lastResponse?.analytics?.correctAnswers}
                    </Typography>
                  </Box>
                  <Typography variant="body1" color="text.secondary" fontWeight={600}>
                    Correct Answers
                  </Typography>
                </CardContent>
              </StatsCard>
              
              <StatsCard color="#f59e0b">
                <CardContent sx={{ p: 3 }}>
                  <Box display="flex" alignItems="center" justifyContent="center" gap={1} mb={2}>
                    <Target sx={{ color: '#f59e0b', fontSize: 24 }} />
                    <Typography variant="h3" fontWeight={800} color="#f59e0b">
                      {Math.round(testState.lastResponse?.analytics?.accuracyRate || 0)}%
                    </Typography>
                  </Box>
                  <Typography variant="body1" color="text.secondary" fontWeight={600}>
                    Accuracy Rate
                  </Typography>
                </CardContent>
              </StatsCard>
            </Box>

            <Box display="flex" gap={3} justifyContent="center" flexWrap="wrap">
              <ModernButton
                onClick={() => window.location.reload()}
                sx={{ fontSize: '1.1rem', py: 1.5, px: 4 }}
              >
                Take Another Test
              </ModernButton>
              <Button
                variant="outlined"
                onClick={() => window.location.href = '/dashboard'}
                sx={{
                  borderColor: '#667eea',
                  color: '#667eea',
                  px: 4,
                  py: 1.5,
                  borderRadius: 2,
                  fontSize: '1.1rem',
                  fontWeight: 600,
                  textTransform: 'none',
                  '&:hover': {
                    borderColor: '#5a67d8',
                    backgroundColor: 'rgba(102, 126, 234, 0.04)',
                  }
                }}
              >
                View Dashboard
              </Button>
            </Box>
          </CardContent>
        </ModernCard>
      </Fade>
    </Container>
  );

  // Main render
  return (
    <TestContainer sx={{ px: { xs: 2, sm: 4, md: 6, lg: 8 } }}>
      {error && (
                  <Container maxWidth="lg" sx={{ mb: 2 }}>
          <Alert severity="error" onClose={() => setError(null)}>
            {error}
          </Alert>
        </Container>
      )}

      {testState.phase === 'setup' && renderSetupForm()}
      {testState.phase === 'testing' && renderTestInterface()}
      {testState.phase === 'explanation' && renderExplanation()}
      {testState.phase === 'completed' && renderCompletion()}

      {/* Active Session Dialog */}
      <ActiveSessionDialog
        open={showActiveSessionDialog}
        session={activeSession}
        onResume={handleResumeSession}
        onEnd={handleEndSession}
        onClose={() => setShowActiveSessionDialog(false)}
        loading={sessionLoading}
      />

      {/* Upgrade Prompt */}
      <UpgradePrompt
        open={showUpgradePrompt}
        onClose={() => setShowUpgradePrompt(false)}
        subject={selectedSubject}
        remainingTests={subscriptionStatus?.testLimits[selectedSubject]?.remaining || 0}
        onUpgradeSuccess={handleUpgradeSuccess}
      />
    </TestContainer>
  );
};

export default AdaptiveTestPage; 